import React, { useEffect, useState, useCallback } from "react";
import { Container, Button, Box, Alert, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  findFund,
  findStrategy,
  TABLE_HEAD_FUND_REBALANCE_BLACKLIST,
} from "../../../../component/Methods";
import LocatesNotFound from "../../Popup/LocatesNotFound";
import Blacklisted from "./components/Blacklisted";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveRequests } from "../../../../Redux/Reducers/selectors";
import { useRebalancerState } from "../../../../hooks/useOptimizedState";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../Redux/Reducers/uiSlice";
import { showComposite } from "../../../../component/Methods/mainFunctions";
import BlacklistedSecuritiesSkeleton from "../../Skeletons/BlacklistedSecuritiesSkeleton";
import { sendWebSocketMessage } from "../../../../hooks/useWebSocket";
import {
  rebalancerStepContainerSx,
  rebalancerStepInnerColumnSx,
} from "../../styles/rebalancerStyles";

const isValidTickerObj = (tickerObj) =>
  tickerObj && typeof tickerObj === "object" && Object.keys(tickerObj).length > 0;

const getCompositeParts = (compositeKey) => {
  const parts = String(compositeKey || "").split("#");
  return {
    strategy: parts[0] || "",
    fund: parts[1] || "",
  };
};

/** Collect tickers for a list key (`blacklistedTickers` | `frozenTickers`) across all funds. */
const combineTickersByKey = (fundsPayload, listKey) => {
  if (!Array.isArray(fundsPayload)) return [];

  return fundsPayload.reduce((acc, fundObj) => {
    const entries = Object.entries(fundObj?.blacklistedTickers || {});
    const mapped = entries.flatMap(([compositeKey, tickerData]) => {
      const list = Array.isArray(tickerData?.[listKey])
        ? tickerData[listKey]
        : [];
      const { strategy, fund } = getCompositeParts(compositeKey);
      return list.filter(isValidTickerObj).map((tickerObj) => ({
        ...tickerObj,
        strategy,
        fund,
      }));
    });
    return acc.concat(mapped);
  }, []);
};

const sumTotals = (fundsPayload) => {
  if (!Array.isArray(fundsPayload)) {
    return { totalBlacklisted: 0, totalFrozen: 0, totalChecked: 0 };
  }

  return fundsPayload.reduce(
    (acc, fundObj) => {
      Object.values(fundObj?.blacklistedTickers || {}).forEach((tickerData) => {
        const blacklistedList = Array.isArray(tickerData?.blacklistedTickers)
          ? tickerData.blacklistedTickers
          : [];
        const frozenList = Array.isArray(tickerData?.frozenTickers)
          ? tickerData.frozenTickers
          : [];

        acc.totalBlacklisted +=
          tickerData?.totalBlacklistedTickers ??
          blacklistedList.filter(isValidTickerObj).length;
        acc.totalFrozen +=
          tickerData?.totalFrozenTickers ??
          frozenList.filter(isValidTickerObj).length;
        acc.totalChecked += tickerData?.totalTickersChecked || 0;
      });
      return acc;
    },
    { totalBlacklisted: 0, totalFrozen: 0, totalChecked: 0 }
  );
};

const mapRowsForTable = (rawList, funds, strategies) => {
  if (!Array.isArray(rawList)) return [];

  return rawList
    .filter(isValidTickerObj)
    .map((item) => {
      const tickerSymbol =
        typeof item?.ticker === "string"
          ? item.ticker
          : item?.ticker?.symbol || "";
      return {
        reason: item?.reason || "",
        ticker: tickerSymbol,
        fund: findFund(item?.fund, funds) || item?.fund || "",
        strategy: findStrategy(item?.strategy, strategies) || item?.strategy || "",
        direction: item?.direction || "",
      };
    });
};

const aggregateTickersForFunds = (rebalancedFunds, blacklistedTickers, listKey) => {
  if (!Array.isArray(rebalancedFunds)) return {};

  return rebalancedFunds.reduce((acc, fundId) => {
    const matchedFund = Array.isArray(blacklistedTickers)
      ? blacklistedTickers.find((item) => item?.fundId === fundId)
      : null;

    if (matchedFund?.blacklistedTickers) {
      const aggregatedTickers = Object.values(matchedFund.blacklistedTickers)
        .flatMap((tickerData) =>
          Array.isArray(tickerData?.[listKey]) ? tickerData[listKey] : []
        )
        .filter(isValidTickerObj)
        .map(({ ticker }) => ({
          ...(ticker && typeof ticker === "object" ? ticker : {}),
        }));

      acc[fundId] = aggregatedTickers;
    } else {
      acc[fundId] = [];
    }

    return acc;
  }, {});
};

const tablesRowSx = {
  display: "flex",
  flexDirection: "row",
  width: "100%",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  gap: 2,
};

const sectionColumnSx = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
  gap: 1,
};

const FundStep1 = ({
  sleeve,
  setCurrentStep,
  rebalanceType,
  rebalancedFunds,
  rebalancedStrategies,
  deltaRebalancer,
  deltaValue,
  driftValue,
}) => {
  const dispatch = useDispatch();
  const { funds, blacklistedTickers, strategies } = useRebalancerState();
  const activeRequests = useSelector(selectActiveRequests);

  const [popupOpen, setPopupOpen] = useState(false);
  const [blacklist, setBlacklistData] = useState([]);
  const [frozen, setFrozenData] = useState([]);
  const [selectedCompositeKey, setSelectedCompositeKey] = useState("all");
  const [CompositeKeys, setCompositeKeys] = useState([]);
  const [blacklistedRaw, setBlacklistedRaw] = useState([]);
  const [frozenRaw, setFrozenRaw] = useState([]);
  const [totalBlacklistedTickers, setTotalBlacklistedTickers] = useState(0);
  const [totalFrozenTickers, setTotalFrozenTickers] = useState(0);
  const [totalTickersChecked, setTotalTickersChecked] = useState(0);

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const applyTotals = useCallback((totals) => {
    setTotalBlacklistedTickers(totals?.totalBlacklisted || 0);
    setTotalFrozenTickers(totals?.totalFrozen || 0);
    setTotalTickersChecked(totals?.totalChecked || 0);
  }, []);

  const loadAllCompositeData = useCallback(() => {
    const safePayload = Array.isArray(blacklistedTickers)
      ? blacklistedTickers
      : [];
    setBlacklistedRaw(combineTickersByKey(safePayload, "blacklistedTickers"));
    setFrozenRaw(combineTickersByKey(safePayload, "frozenTickers"));
    applyTotals(sumTotals(safePayload));
  }, [blacklistedTickers, applyTotals]);

  useEffect(() => {
    const safePayload = Array.isArray(blacklistedTickers)
      ? blacklistedTickers
      : [];

    if (sleeve && safePayload.length > 0) {
      const compositeKeys = safePayload.flatMap((fundObj) =>
        Object.keys(fundObj?.blacklistedTickers || {})
      );
      setCompositeKeys(compositeKeys);
      setSelectedCompositeKey("all");
      setBlacklistedRaw(combineTickersByKey(safePayload, "blacklistedTickers"));
      setFrozenRaw(combineTickersByKey(safePayload, "frozenTickers"));
      applyTotals(sumTotals(safePayload));
    } else {
      setBlacklistedRaw([]);
      setFrozenRaw([]);
      setCompositeKeys([]);
      setSelectedCompositeKey("all");
      applyTotals({ totalBlacklisted: 0, totalFrozen: 0, totalChecked: 0 });
    }
  }, [sleeve, blacklistedTickers, applyTotals]);

  useEffect(() => {
    setBlacklistData(mapRowsForTable(blacklistedRaw, funds, strategies));
  }, [blacklistedRaw, funds, strategies]);

  useEffect(() => {
    setFrozenData(mapRowsForTable(frozenRaw, funds, strategies));
  }, [frozenRaw, funds, strategies]);

  const handleCompositeKeyChange = (e) => {
    const newCompositeKey = e.target.value;
    setSelectedCompositeKey(newCompositeKey);

    const safePayload = Array.isArray(blacklistedTickers)
      ? blacklistedTickers
      : [];

    if (newCompositeKey === "all") {
      loadAllCompositeData();
      return;
    }

    const selectedFund = safePayload.find((fundObj) =>
      Object.keys(fundObj?.blacklistedTickers || {}).includes(newCompositeKey)
    );

    if (!selectedFund) {
      setBlacklistedRaw([]);
      setFrozenRaw([]);
      applyTotals({ totalBlacklisted: 0, totalFrozen: 0, totalChecked: 0 });
      return;
    }

    const tickerData = selectedFund?.blacklistedTickers?.[newCompositeKey] || {};
    const { strategy, fund } = getCompositeParts(newCompositeKey);

    const mapList = (listKey) =>
      (Array.isArray(tickerData?.[listKey]) ? tickerData[listKey] : [])
        .filter(isValidTickerObj)
        .map((tickerObj) => ({
          ...tickerObj,
          fund,
          strategy,
        }));

    const blacklistedList = mapList("blacklistedTickers");
    const frozenList = mapList("frozenTickers");

    setBlacklistedRaw(blacklistedList);
    setFrozenRaw(frozenList);
    setTotalBlacklistedTickers(
      tickerData?.totalBlacklistedTickers ?? blacklistedList.length
    );
    setTotalFrozenTickers(tickerData?.totalFrozenTickers ?? frozenList.length);
    setTotalTickersChecked(tickerData?.totalTickersChecked || 0);
  };

  const onNext = () => {
    try {
      const blacklistedData = aggregateTickersForFunds(
        rebalancedFunds,
        blacklistedTickers,
        "blacklistedTickers"
      );
      const frozenData = aggregateTickersForFunds(
        rebalancedFunds,
        blacklistedTickers,
        "frozenTickers"
      );

      const socketRequest = {
        action: "create_rebalancer_items",
        data: {
          fundIds: rebalancedFunds,
          blacklistedTickers: blacklistedData,
          frozenTickers: frozenData,
          rebalancerType: rebalanceType === "all" ? 1 : 0,
          deltaRebalancer: Number(deltaRebalancer),
          deltaValue: Number(deltaValue),
          strategies: rebalancedStrategies,
          driftValue: Number(driftValue),
        },
      };
      sendWebSocketMessage(JSON.stringify(socketRequest));
      dispatch(addActiveRequests("create_rebalancer_items"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Creating Rebalancer Items"));
      setCurrentStep((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to continue rebalancer step 1:", err);
    }
  };

  const isLoadingBlacklistedData = activeRequests.includes(
    "rebalancer_check_blacklist"
  );

  if (isLoadingBlacklistedData) {
    return <BlacklistedSecuritiesSkeleton />;
  }

  const blacklistCountLabel =
    totalBlacklistedTickers > 0
      ? `: ${totalBlacklistedTickers} / ${totalTickersChecked}`
      : "";
  const frozenCountLabel =
    totalFrozenTickers > 0
      ? `: ${totalFrozenTickers} / ${totalTickersChecked}`
      : "";

  return (
    <Container disableGutters maxWidth="100%" sx={rebalancerStepContainerSx}>
      <Box sx={rebalancerStepInnerColumnSx}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: "40%" }}>
            <Box
              sx={{
                marginBottom: 1,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {sleeve && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <select
                    value={selectedCompositeKey}
                    onChange={handleCompositeKeyChange}
                    style={{
                      padding: "5px",
                      fontSize: "14px",
                      marginBottom: 10,
                    }}
                    size="small"
                  >
                    <option value="all">All</option>
                    {(CompositeKeys || []).map((compositeKey) => (
                      <option key={compositeKey} value={compositeKey}>
                        {showComposite(compositeKey, strategies, funds)}
                      </option>
                    ))}
                  </select>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={onNext}
              sx={{ width: "150px", height: "40px" }}
            >
              Next
            </Button>
          </Box>
        </Box>

        {/* Blacklisted + Frozen side by side */}
        <Box sx={tablesRowSx}>
          <Box sx={sectionColumnSx}>
            <Typography sx={{ fontWeight: "bold", fontSize: "20px", flexShrink: 0 }}>
              Blacklisted Securities{blacklistCountLabel}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <Alert severity="info">
                The securities that appear below will not be included in final
                rebalancer trades.
              </Alert>
            </Box>
            {blacklist?.length > 0 ? (
              <Blacklisted
                rows={blacklist}
                tableHeader={TABLE_HEAD_FUND_REBALANCE_BLACKLIST}
              />
            ) : (
              <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                No blacklisted tickers found
              </Typography>
            )}
          </Box>

          <Box sx={sectionColumnSx}>
            <Typography sx={{ fontWeight: "bold", fontSize: "20px", flexShrink: 0 }}>
              Frozen Tickers{frozenCountLabel}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <Alert severity="warning">
              Hard freeze — These tickers cannot be traded and are excluded from rebalancer trades.

              </Alert>
            </Box>
            {frozen?.length > 0 ? (
              <Blacklisted
                rows={frozen}
                tableHeader={TABLE_HEAD_FUND_REBALANCE_BLACKLIST}
              />
            ) : (
              <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                No frozen tickers found
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <LocatesNotFound open={popupOpen} onClose={handleClosePopup} />
    </Container>
  );
};

export default FundStep1;
